import type { ComponentPropsWithRef, ElementType, ReactNode } from 'react';

export type ButtonVariant = 'text' | 'outlined' | 'contained';
export type ButtonColor = 'primary' | 'secondary' | 'error' | 'inherit';
export type ButtonSize = 'small' | 'medium' | 'large';

type ButtonOwnProps = {
  variant?: ButtonVariant;
  color?: ButtonColor;
  size?: ButtonSize;
  fullWidth?: boolean;
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  className?: string;
  children?: ReactNode;
};

type ButtonRoot = 'button' | 'a' | 'label' | Exclude<ElementType, string>;

export type ButtonProps<C extends ButtonRoot = 'button'> = ButtonOwnProps & {
  component?: C;
} & Omit<ComponentPropsWithRef<C>, keyof ButtonOwnProps | 'component'>;

const VARIANT_CLASS: Record<ButtonVariant, string> = {
  text: 'ui-btn--text',
  outlined: 'ui-btn--outlined',
  contained: 'ui-btn--contained'
};

const COLOR_CLASS: Record<ButtonColor, string> = {
  primary: 'ui-btn--primary',
  secondary: 'ui-btn--secondary',
  error: 'ui-btn--error',
  inherit: 'ui-btn--inherit'
};

const SIZE_CLASS: Record<ButtonSize, string> = {
  small: 'ui-btn--small',
  medium: 'ui-btn--medium',
  large: 'ui-btn--large'
};

export default function Button<C extends ButtonRoot = 'button'>({
  component,
  variant = 'text',
  color = 'primary',
  size = 'medium',
  fullWidth = false,
  startIcon,
  endIcon,
  className,
  children,
  ...rest
}: ButtonProps<C>) {
  const ButtonComponent = (component ?? 'button') as ElementType;

  // A native <button> defaults to type="submit"; MUI's ButtonBase defaults it to "button".
  // Without this, every unqualified Button inside a <form> would start submitting it.
  const typeAttr =
    ButtonComponent === 'button' && !('type' in rest) ? { type: 'button' as const } : undefined;

  const classes = [
    'ui-btn',
    VARIANT_CLASS[variant],
    COLOR_CLASS[color],
    SIZE_CLASS[size],
    fullWidth && 'ui-btn--full-width',
    className
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <ButtonComponent className={classes} {...typeAttr} {...rest}>
      {startIcon ? <span className="ui-btn__icon ui-btn__icon--start">{startIcon}</span> : null}
      {children}
      {endIcon ? <span className="ui-btn__icon ui-btn__icon--end">{endIcon}</span> : null}
    </ButtonComponent>
  );
}
